import React from "react";
import { Pin } from "lucide-react";
import BaseModel from "../../models/BaseModel";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import { canPinNavigatorSelection, navigatorPinDropObject } from "./navigatorPinObject";

type NavigatorPinProps = {
  root: BaseModel;
  excluded?: BaseModel | readonly BaseModel[];
  pinned?: BaseModel;
  onPin: (object: BaseModel | undefined) => void;
};

/** Provides a drop target and a selection shortcut for this navigator's temporary root. */
export default function NavigatorPin({ root, excluded, pinned, onPin }: NavigatorPinProps) {
  useOnChangeWatcher(state.selectionManager);
  const selected = state.selectionManager.selectedObject;
  const canPin = canPinNavigatorSelection(selected, root, excluded);

  function resolveObject(id: string): BaseModel | undefined {
    const object = state.objectRegistry.getObject(id);
    return object instanceof BaseModel ? object : undefined;
  }

  return (
    <>
      {pinned && (
        <button
          type="button"
          className="navigatorPin navigatorLevelUp"
          aria-label="Eine Ebene höher"
          title="Eine Ebene höher"
          data-testid="navigator-level-up"
          disabled={!(pinned.parent instanceof BaseModel) || pinned.parent.isDeleted}
          onClick={() => {
            if (pinned.parent instanceof BaseModel && !pinned.parent.isDeleted) onPin(pinned.parent);
          }}
        >
          <svg width={17.6} height={17.6} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 2 1 9h5v10a4 4 0 0 0 4 4h10a2 2 0 0 0 0-4H10V9h5Z" />
          </svg>
        </button>
      )}{" "}
      <button
        type="button"
        className="navigatorPin"
        aria-label={pinned ? "Pin lösen" : "Ausgewähltes Objekt anpinnen"}
        aria-pressed={!!pinned}
        data-tooltip-id="tv-tooltip"
        data-tooltip-content="aktuelles Objekt Pinnen"
        data-tooltip-place="top" // no itx
        onClick={() => {
          if (pinned) onPin(undefined);
          else if (canPin) onPin(selected);
        }}
        onDragOver={(event) => {
          event.stopPropagation();
          const object = navigatorPinDropObject(state.dragData ?? "", resolveObject);
          if (
            !object ||
            !event.dataTransfer.types.includes(
              "text/json", // no itx
            )
          )
            return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy"; // no itx
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const object = navigatorPinDropObject(event.dataTransfer.getData("text/json"), resolveObject);
          if (object) onPin(object);
        }}
      >
        <Pin size={22} strokeWidth={1.6} fill={pinned ? "currentColor" : "none"} />
      </button>
    </>
  );
}
